import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';

const BASE = 'https://omwuihwgfpewephqawdh.supabase.co/functions/v1';
const RSS_URL = `${BASE}/blog-rss`;
const SITEMAP_URL = `${BASE}/sitemap`;

const RFC822 =
  /^(Mon|Tue|Wed|Thu|Fri|Sat|Sun), \d{2} (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4} \d{2}:\d{2}:\d{2} (GMT|[+-]\d{4})$/;

type Check = { label: string; pass: boolean; detail?: string };
type RssItem = {
  title: string;
  link: string;
  pubDate: string;
  guid: string;
  isPermaLink: string | null;
  categories: string[];
  checks: Check[];
};
type SitemapEntry = { loc: string; lastmod: string; checks: Check[] };

function getText(node: Element | null) {
  return (node?.textContent || '').trim();
}

function parseRss(xml: string): { items: RssItem[]; channelTitle: string } {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const channelTitle = getText(doc.querySelector('channel > title'));
  const items: RssItem[] = [];
  doc.querySelectorAll('item').forEach((it) => {
    const title = getText(it.querySelector('title'));
    const link = getText(it.querySelector('link'));
    const pubDate = getText(it.querySelector('pubDate'));
    const guidEl = it.querySelector('guid');
    const guid = getText(guidEl);
    const isPermaLink = guidEl?.getAttribute('isPermaLink') ?? null;
    const categories = Array.from(it.querySelectorAll('category')).map((c) =>
      getText(c)
    );
    const checks: Check[] = [
      { label: 'Title present', pass: !!title },
      {
        label: 'HTTPS /blogs/ link',
        pass: /^https:\/\/.+\/blogs\/.+/i.test(link),
        detail: link,
      },
      {
        label: 'pubDate is RFC-822',
        pass: RFC822.test(pubDate),
        detail: pubDate,
      },
      {
        label: 'guid isPermaLink="true"',
        pass: isPermaLink === 'true',
        detail: `isPermaLink=${isPermaLink}`,
      },
      {
        label: 'guid matches link',
        pass: guid === link,
        detail: guid,
      },
      {
        label: 'Has categories',
        pass: categories.length > 0,
        detail: categories.join(', '),
      },
    ];
    items.push({ title, link, pubDate, guid, isPermaLink, categories, checks });
  });
  return { items, channelTitle };
}

function parseSitemap(xml: string): SitemapEntry[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const entries: SitemapEntry[] = [];
  doc.querySelectorAll('url').forEach((u) => {
    const loc = getText(u.querySelector('loc'));
    const lastmod = getText(u.querySelector('lastmod'));
    const checks: Check[] = [
      { label: 'HTTPS loc', pass: /^https:\/\//i.test(loc), detail: loc },
      {
        label: 'lastmod ISO',
        pass: !lastmod || /^\d{4}-\d{2}-\d{2}/.test(lastmod),
        detail: lastmod || '(none)',
      },
    ];
    entries.push({ loc, lastmod, checks });
  });
  return entries;
}

export default function FeedValidator() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rss, setRss] = useState<{ items: RssItem[]; channelTitle: string } | null>(null);
  const [sitemap, setSitemap] = useState<SitemapEntry[] | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rssXml, sitemapXml] = await Promise.all([
        fetch(RSS_URL).then((r) => r.text()),
        fetch(SITEMAP_URL).then((r) => r.text()),
      ]);
      setRss(parseRss(rssXml));
      setSitemap(parseSitemap(sitemapXml));
    } catch (e: any) {
      setError(e.message || 'Failed to fetch feeds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
  }, []);

  const rssFails = rss?.items.reduce(
    (n, it) => n + it.checks.filter((c) => !c.pass).length,
    0
  ) ?? 0;
  const sitemapFails =
    sitemap?.reduce((n, u) => n + u.checks.filter((c) => !c.pass).length, 0) ?? 0;
  const blogInSitemap = sitemap?.filter((u) => /\/blogs\//.test(u.loc)).length ?? 0;
  const crossCheckFails =
    rss && sitemap
      ? rss.items.filter(
          (it) => !sitemap.some((u) => u.loc === it.link)
        ).length
      : 0;

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <SEOHead
        title="Feed Validator"
        description="Developer-facing validation for RSS and sitemap feeds."
        noindex
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Feed Validator</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Inspects the live RSS and sitemap edge functions and reports issues.
          </p>
        </div>
        <Button onClick={run} disabled={loading} variant="outline">
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Re-check
        </Button>
      </div>

      {error && (
        <Card className="mb-4 border-destructive">
          <CardContent className="pt-6 flex items-center gap-2 text-destructive">
            <XCircle className="h-5 w-5" /> {error}
          </CardContent>
        </Card>
      )}

      {!loading && rss && sitemap && (
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            label="RSS items"
            value={rss.items.length}
            ok={rssFails === 0}
            sub={rssFails === 0 ? 'All checks pass' : `${rssFails} issue(s)`}
          />
          <SummaryCard
            label="Sitemap URLs"
            value={sitemap.length}
            ok={sitemapFails === 0}
            sub={`${blogInSitemap} blog URLs · ${
              sitemapFails === 0 ? 'OK' : `${sitemapFails} issue(s)`
            }`}
          />
          <SummaryCard
            label="RSS ↔ sitemap"
            value={crossCheckFails === 0 ? 'In sync' : `${crossCheckFails} missing`}
            ok={crossCheckFails === 0}
            sub="Each RSS link should exist in sitemap"
          />
        </div>
      )}

      {rss && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              RSS Feed
              <Badge variant="outline">{RSS_URL}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rss.items.map((it, i) => (
              <ItemBlock
                key={i}
                title={it.title}
                meta={it.pubDate}
                checks={it.checks}
                extra={
                  it.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {it.categories.map((c) => (
                        <Badge key={c} variant="secondary" className="text-xs">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  )
                }
              />
            ))}
          </CardContent>
        </Card>
      )}

      {sitemap && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Sitemap
              <Badge variant="outline">{SITEMAP_URL}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[480px] overflow-auto space-y-2">
              {sitemap.map((u, i) => (
                <ItemBlock
                  key={i}
                  title={u.loc}
                  meta={u.lastmod ? `lastmod: ${u.lastmod}` : ''}
                  checks={u.checks}
                  compact
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  ok,
  sub,
}: {
  label: string;
  value: number | string;
  ok: boolean;
  sub: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold">{value}</div>
            <div className="text-xs text-muted-foreground mt-1">{sub}</div>
          </div>
          {ok ? (
            <CheckCircle2 className="h-7 w-7 text-green-600" />
          ) : (
            <AlertTriangle className="h-7 w-7 text-amber-500" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ItemBlock({
  title,
  meta,
  checks,
  extra,
  compact,
}: {
  title: string;
  meta?: string;
  checks: Check[];
  extra?: React.ReactNode;
  compact?: boolean;
}) {
  const allPass = checks.every((c) => c.pass);
  return (
    <div
      className={`border rounded-lg p-3 ${
        allPass ? 'border-border' : 'border-amber-500/50 bg-amber-500/5'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`font-medium truncate ${compact ? 'text-sm' : ''}`}>
            {title || '(empty)'}
          </div>
          {meta && (
            <div className="text-xs text-muted-foreground mt-0.5">{meta}</div>
          )}
        </div>
        {allPass ? (
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
        ) : (
          <XCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
        )}
      </div>
      {!compact && (
        <div className="grid sm:grid-cols-2 gap-1 mt-2">
          {checks.map((c, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              {c.pass ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-destructive" />
              )}
              <span>{c.label}</span>
              {c.detail && (
                <span className="truncate opacity-70">— {c.detail}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {compact && !allPass && (
        <div className="text-xs text-destructive mt-1">
          {checks.filter((c) => !c.pass).map((c) => c.label).join(' · ')}
        </div>
      )}
      {extra && <div className="mt-2">{extra}</div>}
    </div>
  );
}
